import React, { useState, useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import Breadcrumb from '../../../component/common/Breadcrumb';
import { getWorkoutPlanById } from '../../../api/healthAPI';
import { getLocalStorageItem } from '../../../utils/helper';
import {
  WorkoutPlanContextProvider,
  useWorkoutPlanCreation
} from '../../../context/WorkoutPlanContext';
import WorkoutPlanDetailsTab from '../../../component/HealthComponent/WorkoutPlanForm/WorkoutPlanDetailsTab';
import WorkoutTile from '../../../component/HealthComponent/workoutTile';

const ViewWorkoutPlansContent = ({ isPreview = false, onExitPreview }) => {
  const { id } = useParams();
  const userData = getLocalStorageItem('userData') && JSON.parse(getLocalStorageItem('userData'));
  const { planData: contextPlanData } = useWorkoutPlanCreation();

  const [workoutPlan, setWorkoutPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedDays, setExpandedDays] = useState({});

  const breadcrumbPages = useMemo(
    () => [
      {
        name: 'Health Dashboard',
        href: `${userData?.slug ? `/${userData.slug}` : ''}/health/dashboard`
      },
      {
        name: 'Workout Plans',
        href: `${userData?.slug ? `/${userData.slug}` : ''}/health/workout-plans`
      },
      { name: 'Workout Plan Preview', current: true }
    ],
    [userData?.slug]
  );

  useEffect(() => {
    const fetchWorkoutPlan = async () => {
      // If in preview mode and no id, use context data
      if (isPreview && !id) {
        console.log('Using context workout plan data for preview:', contextPlanData);

        // Format context data for display
        const formattedPlan = {
          _id: contextPlanData.workoutPlanId,
          plan_name: contextPlanData.plan_name,
          plan_description: contextPlanData.plan_description,
          workout_focus: contextPlanData.workout_focus,
          location: contextPlanData.location,
          difficulty: contextPlanData.difficulty,
          thumbnail_url: contextPlanData.thumbnail_url,
          equipments: contextPlanData.equipments || [],
          days:
            contextPlanData.schedule?.map((day, index) => ({
              day_name: `Day ${day.day_number}`,
              day_sequence: day.day_number,
              workout_ids: day.workouts || []
            })) || []
        };

        setWorkoutPlan(formattedPlan);

        // Initialize all days as expanded
        const initialExpandedState = {};
        formattedPlan.days?.forEach((_, index) => {
          initialExpandedState[index] = true;
        });
        setExpandedDays(initialExpandedState);

        setLoading(false);
        return;
      }

      if (!id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await getWorkoutPlanById(id);
        if (response?.meta?.code === 1 && response?.data) {
          setWorkoutPlan(response.data);

          // Initialize all days as expanded
          const initialExpandedState = {};
          response.data.days?.forEach((_, index) => {
            initialExpandedState[index] = true;
          });
          setExpandedDays(initialExpandedState);
        }
      } catch (err) {
        console.error('Error fetching workout plan:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchWorkoutPlan();
  }, [id, isPreview, contextPlanData]);

  const toggleDay = (dayIndex) => {
    setExpandedDays((prev) => ({
      ...prev,
      [dayIndex]: !prev[dayIndex]
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!workoutPlan) {
    return (
      <div className="relative lg:mb-[45px]">
        <Helmet>
          <title>Workout Plan Preview | Shoorah Admin</title>
        </Helmet>
        <Breadcrumb pageList={breadcrumbPages} className="lg:mt-8" />
        <div className="bg-white rounded-2xl p-10 mt-6 shadow-sm text-center">
          <h2 className="text-xl font-semibold text-[#0B1D2E] mb-2">Workout Plan not found</h2>
          <p className="text-sm text-gray-500 mb-6">
            The workout plan you are trying to view does not exist or has been removed.
          </p>
          <Link
            to={`${userData?.slug ? `/${userData.slug}` : ''}/health/workout-plans`}
            className="inline-flex items-center px-6 py-2 bg-shoorah-active text-white rounded-full text-sm"
          >
            Back to Workout Plans
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="relative lg:mb-[45px]">
      <Helmet>
        <title>{`${workoutPlan?.plan_name || 'Workout Plan'} | Preview`} | Shoorah Admin</title>
      </Helmet>

      <Breadcrumb pageList={breadcrumbPages} className="lg:mt-8" />

      <div className="mt-6 flex flex-col gap-6">
        {/* Top row: Thumbnail and Edit Button - Transparent Background */}
        <div className="flex items-start justify-between gap-6">
          <div className="w-[280px] flex-shrink-0">
            <div className="rounded-3xl overflow-hidden shadow-sm aspect-[4/3]">
              {workoutPlan?.thumbnail_url ? (
                <img
                  src={workoutPlan.thumbnail_url}
                  alt={workoutPlan?.plan_name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-sm text-gray-500 bg-gray-100">
                  No Thumbnail
                </div>
              )}
            </div>
          </div>
          {!isPreview && (
            <Link
              to={`${userData?.slug ? `/${userData.slug}` : ''}/health/workout-plans/new?id=${id}`}
              state={{ _id: id, action: 'edit' }}
              className="inline-flex items-center justify-center px-5 py-2.5 text-sm font-medium text-white bg-shoorah-active rounded-full shadow-sm hover:opacity-90 transition"
            >
              Edit Workout Plan
            </Link>
          )}
        </div>

        {/* Workout Plan Details Section */}
        <WorkoutPlanDetailsTab isView={true} />

        {/* Schedule Section with Accordion */}
        <div className="bg-white dark:bg-shoorah-darkBgTabColor rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Schedule</h3>

          {workoutPlan?.days && workoutPlan.days.length > 0 ? (
            <div className="space-y-4">
              {workoutPlan.days.map((day, dayIndex) => (
                <div
                  key={dayIndex}
                  className="border border-gray-200 dark:border-shoorah-darkBorder rounded-lg overflow-hidden"
                >
                  {/* Accordion Header */}
                  <button
                    onClick={() => toggleDay(dayIndex)}
                    className="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-shoorah-darkBgColor hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <h4 className="text-base font-semibold text-gray-900 dark:text-white">
                        {day.day_name}
                      </h4>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        ({day.workout_ids?.length || 0}{' '}
                        {day.workout_ids?.length === 1 ? 'workout' : 'workouts'})
                      </span>
                    </div>
                    {expandedDays[dayIndex] ? (
                      <ChevronUpIcon className="w-5 h-5 text-gray-500" />
                    ) : (
                      <ChevronDownIcon className="w-5 h-5 text-gray-500" />
                    )}
                  </button>

                  {/* Accordion Content */}
                  {expandedDays[dayIndex] && (
                    <div className="p-6 bg-white dark:bg-shoorah-darkBgTabColor">
                      {day.workout_ids && day.workout_ids.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                          {day.workout_ids.map((workout) => (
                            <WorkoutTile
                              key={workout._id}
                              image={workout.cover_image_url}
                              title={workout.workout_name}
                              usageCount={workout.view_count || 0}
                              badgeText={workout.difficulty || workout.workout_focus || 'Workout'}
                              onClick={() => {}}
                            />
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          No workouts assigned to this day.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-gray-50 dark:bg-shoorah-darkBgColor rounded-lg">
              <p className="text-gray-500 dark:text-gray-400">No workout days scheduled yet.</p>
            </div>
          )}
        </div>

        {/* Preview Mode Action Buttons */}
        {isPreview && onExitPreview && (
          <div className="flex justify-end gap-4 mt-6">
            <button
              type="button"
              onClick={onExitPreview}
              className="px-8 py-2 border border-gray-300 bg-white text-gray-700 rounded-full text-sm font-medium hover:bg-gray-50 transition-colors"
            >
              Back to Editing
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const ViewWorkoutPlans = ({ isPreview = false, onExitPreview }) => {
  // When used in preview mode from NewWorkoutPlans, it will use the parent's context
  // When used standalone (direct route), we need to provide context
  if (isPreview) {
    return <ViewWorkoutPlansContent isPreview={isPreview} onExitPreview={onExitPreview} />;
  }

  return (
    <WorkoutPlanContextProvider>
      <ViewWorkoutPlansContent isPreview={isPreview} onExitPreview={onExitPreview} />
    </WorkoutPlanContextProvider>
  );
};

export default ViewWorkoutPlans;
